/**
 * exercise-row-supervisor.js
 */

import React from 'react';
import { useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { 
  handlerExerciseCompletionStatus, 
  supervisorExerciseCompletionStatus,
  supervisorReviewStatus,
} from 'utils/record-status';
import useRecordStore from 'contexts/record-store';
import Icon from 'components/icon';
import IconPatrolDetection from 'components/icon-patrol-detection';
import CompletionStatusIcon from 'components/record-status-icons/completion';
import ReviewStatusIconExerciseSuper from 'components/record-status-icons/review/review-status-icon-exercise-super';
import {
  getOdorSummary,
  getPatrolSummary,
  shrinkOptionalWidth
} from '../helpers';
import Row from '../row/row';
import RowStatusChip from '../row/row-status-chip';
import RowStatusTooltip from '../row/row-status-tooltip';


const useStyles = makeStyles(theme => ({
  completionChip: {
    minWidth: 120,
  },
  reviewChip: {
    minWidth: 120
  },
}));


export default function ExerciseRowSupervisor(props) {
  const {
    id,
    exercise,
    event,
    basePath,
    isSelected,
    onSelectionChange,
    isFuture,
    workMode,
    workModeName,
    onViewReport,
  } = props;
  const classes = useStyles();
  const isChipSize = useMediaQuery(theme => theme.breakpoints.up(shrinkOptionalWidth), { noSsr: true });
  const recordStore = useRecordStore();
  const { t } = useTranslation('general');
  const customCompare = `${exercise.reviewStats.notReviewed}-${exercise.reviewStats.accepted}-${exercise.reviewStats.rejected}`;
  const isReportDisabled = exercise.userStats.reportableAnswerIds.length === 0;
  const reportDisabledReason = exercise.userStats.reportableAnswerIds.length === 0
    ? <>Reports can only be created if there are<br />handlers who have printable records<br />for this exercise.</>
    : '';
  const completionStatusParams = React.useMemo(() => {
    return supervisorExerciseCompletionStatus(
      exercise.userStats.complete,
      exercise.userStats.incomplete,
      event.date
    );
  }, [exercise.userStats.complete, exercise.userStats.incomplete, event.date]);
  const reviewStatusParams = React.useMemo(() => {
    return supervisorReviewStatus(exercise.reviewStats)
  }, [exercise.reviewStats]);


  // efficiently get answer collection fields
  const answerRecords = recordStore.useRecordArray(Types.Record.ANSWER, exercise.answers);
  const { answerToReview } = (() => {
    let answerToReview = null;

    for (let i = 0; i < answerRecords.length; i++) {
      const a = answerRecords[i].get();

      // look for first reviewable answer
      if (answerToReview === null && a.review?.status === Types.ReviewStatus.NOT_REVIEWED) {
        answerToReview = a;
      }
    }

    return {
      answerToReview,
    };
  })();


  const to = makeLocation(
    basePath,
    'training',
    answerToReview ? 'viewHandlerAnswer' : 'viewExercise',
    answerToReview
      ? { exerciseId: id, handlerId: answerToReview.handler.id, answerId: answerToReview.id }
      : { exerciseId: id }
  );
  const routeLink = <RouterLink to={to} className='absolute-fill record-link' />;


  // completion status tooltip
  const completionStatusTooltipItems = !isChipSize
    ? null 
    : exercise.managedHandlers?.map(handler => {
      const params = handlerExerciseCompletionStatus(
        handler.userStats.complete,
        handler.userStats.incomplete,
        0, // supers don't care about outdated
        event,
        handler.id,
        handler.answers.length,
        true
      );
      return {
        id: handler.id,
        icon: <Icon type={params.id} className={`icon-${params.id}`} />,
        name: handler.name,
        status: params.label
      };
    });


  // review status tooltip
  const reviewStatusTooltipItems = !isChipSize
    ? null
    : exercise.managedHandlers
      ?.filter(handler => handler.userStats.complete > 0)
      ?.map(handler => {
        const params = supervisorReviewStatus(handler.reviewStats);
        return {
          id: handler.id,
          icon: <Icon type={params.id} className={`icon-${params.id}`} />,
          name: handler.name,
          status: params.label
        }
      });


  // menu items
  const menuItems = [
    // view
    {
      title: 'View',
      iconType: 'view',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'viewExercise', { exerciseId: id }, null),
      isDefaultItem: true
    },

    // view report
    {
      title: 'View Report',
      iconType: 'report',
      disabled: isReportDisabled,
      disabledReason: reportDisabledReason,
      submenuItems: [{
        title: 'All Handlers',
        iconType: 'group',
        onSelect: () => onViewReport(Types.RecordGroup.TRAINING, exercise.userStats.reportableAnswerIds),
        tooltip: exercise.userStats.outdated > 0
          ? `One or more handlers have outdated records which will not be included in the report`
          : ''
      }, ...(exercise.managedHandlers
        // filter out not-attending
        ?.filter(handler => {
          const member = event.members.find(m => m.id === handler.id);
          return member && (!member.isOptional || member.isAttending || member.isAttending === null);
        })
        ?.map(handler => ({
          title: handler.name,
          iconType: 'handler',
          onSelect: () => onViewReport(Types.RecordGroup.TRAINING, handler.userStats.reportableAnswerIds),
          disabled: handler.userStats.reportableAnswerIds.length === 0,
          disabledReason: handler.userStats.reportableAnswerIds.length === 0
            ? `${handler.name} has no printable records`
            : '',
          tooltip: handler.userStats.outdated > 0
            ? `${handler.name} has ${handler.userStats.outdated} outdated record${handler.userStats.outdated === 1 ? '' : 's'} which will not be included in the report`
            : ''
        })) || [])]
    }
  ];


  return (
    <>
      {exercise &&
        <Row
          data-testid={id}
          data-testtype='record-exercise'
          recordType={Types.Record.EXERCISE}
          className='content'
          routeLink={routeLink}

          selection={{
            type: Types.RecordGroup.TRAINING,
            id,
            isSelected,
            isDisabled: isReportDisabled,
            disabledReason: reportDisabledReason,
            onChange: onSelectionChange
          }}

          icon={<IconPatrolDetection
            workMode={exercise.workMode}
            patrolType={workMode === Types.WorkMode.PATROL && exercise.patrol.patrolTypes.length === 1
              ? exercise.patrol.patrolTypes[0].type
              : Types.Patrol.SCENARIO}
          />}
          title={exercise.name}
          detail={`${workModeName}: ${workMode === Types.WorkMode.PATROL
            ? getPatrolSummary(Types.Record.EXERCISE, exercise.patrol.patrolTypes, t)
            : getOdorSummary(exercise.detection.environments)}`}

          optional={
            <div className='flex-hc record-status'>
              {/* review chip - desktop */}
              <span className='status-desktop review-status'>
                {(!isFuture && reviewStatusParams) &&
                <RowStatusTooltip items={reviewStatusTooltipItems}>
                  <RowStatusChip
                    className={`chip-${reviewStatusParams.id} chip-font-${reviewStatusParams.id} ${classes.reviewChip}`}
                    label={<><Icon type={reviewStatusParams.id} />{reviewStatusParams.label}</>}
                    to={to}
                  />
                </RowStatusTooltip>}
              </span>
              {/* review icon - mobile */}
              <span className='status-mobile review-status'>
                <ReviewStatusIconExerciseSuper
                  reviewStats={exercise.reviewStats}
                  isAvailable={exercise.userStats.complete > 0}
                  to={to}
                />
              </span>

              {/* completion chip - desktop */}
              <span className='status-desktop'>
                <RowStatusTooltip items={completionStatusTooltipItems}>
                  <RowStatusChip
                    className={`chip-${completionStatusParams.id} chip-font-${completionStatusParams.id} ${classes.completionChip}`}
                    label={<><Icon type={completionStatusParams.id} />{completionStatusParams.label}</>}
                    to={to}
                  />
                </RowStatusTooltip>
              </span>
              {/* completion icon - mobile */}
              <span className='status-mobile'>
                <CompletionStatusIcon
                  params={completionStatusParams}
                  to={to}
                />
              </span>
            </div>}

          menu={{
            ariaLabel: 'exercise menu options',
            className: '',
            items: menuItems
          }}

          customCompare={customCompare}
        />}
    </>
  )
}